import { isValidSolanaAddress } from "./wallet";

export type ParsedAssetList = {
  mints: string[];
  invalid: string[];
};

const ASSET_URL_RE = /\/assets\/solana\/([1-9A-HJ-NP-Za-km-z]{32,44})/;
// Solana mint address (base58, 32-44 chars). Matches anywhere in a
// token so trailing query strings or punctuation don't kill the line.
const BARE_MINT_RE = /[1-9A-HJ-NP-Za-km-z]{32,44}/;

// Splits on any whitespace, commas, semicolons, or pipes. Users tend
// to paste lists in all sorts of shapes.
const TOKEN_SPLIT_RE = /[\s,;|]+/;

// Pulls mint addresses out of arbitrary user input. Accepts:
//   - full collectorcrypt.com asset URLs
//   - any URL with /assets/solana/<mint> in the path
//   - bare mint addresses
//   - mixed lines, with any of whitespace/comma/semicolon/pipe as separator
// Dedupes while preserving first-seen order. Unrecognized tokens are
// reported in `invalid` so the UI can flag them.
export function parseAssetList(raw: string): ParsedAssetList {
  const seen = new Set<string>();
  const mints: string[] = [];
  const invalid: string[] = [];

  for (const token of raw.split(TOKEN_SPLIT_RE)) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    const urlMatch = trimmed.match(ASSET_URL_RE);
    let candidate: string | null = null;
    if (urlMatch) {
      candidate = urlMatch[1] ?? null;
    } else if (isValidSolanaAddress(trimmed)) {
      candidate = trimmed;
    } else {
      // Last-ditch: pull a mint out of arbitrary text (e.g. solscan URL).
      const bare = trimmed.match(BARE_MINT_RE);
      if (bare && isValidSolanaAddress(bare[0])) candidate = bare[0];
    }

    if (!candidate || !isValidSolanaAddress(candidate)) {
      invalid.push(trimmed);
      continue;
    }
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    mints.push(candidate);
  }

  return { mints, invalid };
}
